import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Logo } from '../components/Logo';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { 
  Leaf, Shield, Users, CheckCircle2, XCircle, 
  Loader2, Mail, Building2, FileText, Clock,
  BarChart3, TrendingUp, Activity, AlertCircle,
  Link2, Plus, Trash2, Edit, ShoppingCart, ExternalLink,
  ToggleLeft, ToggleRight, MousePointerClick, Search, User,
  Key, Settings, FolderOpen, Download, File, MessageSquare, Star, Eye
} from 'lucide-react';
import { toast } from 'sonner';
import FeedbackCampaignCard from '../components/admin/FeedbackCampaignCard';
import AnnouncementCampaignCard from '../components/admin/AnnouncementCampaignCard';
import RetailersTab from '../components/admin/RetailersTab';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminPage() {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [adminSecret, setAdminSecret] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [pendingProviders, setPendingProviders] = useState([]);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [stats, setStats] = useState(null);
  const [activeTab, setActiveTab] = useState('providers');
  
  // Affiliate state
  const [affiliateSettings, setAffiliateSettings] = useState(null);
  const [affiliateLinks, setAffiliateLinks] = useState([]);
  const [affiliateAnalytics, setAffiliateAnalytics] = useState(null);
  const [showAddLink, setShowAddLink] = useState(false);
  const [editingLink, setEditingLink] = useState(null);
  const [newLink, setNewLink] = useState({
    supplement_name: '',
    provider: 'amazon',
    affiliate_url: '',
    display_name: '',
    commission_rate: 0,
    enabled: true
  });
  
  // Users state
  const [users, setUsers] = useState([]);
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [deletingUserId, setDeletingUserId] = useState(null);
  
  // Documents state
  const [documents, setDocuments] = useState([]);
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  
  // Settings state
  const [currentSecret, setCurrentSecret] = useState('');
  const [newSecret, setNewSecret] = useState('');
  const [confirmSecret, setConfirmSecret] = useState('');
  const [changingSecret, setChangingSecret] = useState(false);
  
  // Testimonials state
  const [testimonials, setTestimonials] = useState([]);
  const [testimonialStats, setTestimonialStats] = useState({ pending: 0, approved: 0, rejected: 0 });
  const [testimonialFilter, setTestimonialFilter] = useState('pending');
  const [loadingTestimonials, setLoadingTestimonials] = useState(false);

  const adminToken = localStorage.getItem('admin_token');

  useEffect(() => {
    if (adminToken) {
      setIsAuthenticated(true);
      fetchData();
    }
  }, [adminToken]);

  const fetchData = async () => {
    setIsLoading(true);
    // Always read token fresh from localStorage to avoid stale closure
    const token = localStorage.getItem('admin_token');
    if (!token) {
      setIsLoading(false);
      return;
    }
    try {
      const [providersRes, statsRes, affiliateSettingsRes, affiliateLinksRes, affiliateAnalyticsRes, usersRes, testimonialStatsRes] = await Promise.all([
        axios.get(`${API}/admin/pending-providers`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/admin/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/affiliate/settings`),
        axios.get(`${API}/admin/affiliate/links`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/admin/affiliate/analytics`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/admin/users?limit=100`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/testimonials/admin/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setPendingProviders(providersRes.data.providers || []);
      setStats(statsRes.data);
      setAffiliateSettings(affiliateSettingsRes.data);
      setAffiliateLinks(affiliateLinksRes.data.links || []);
      setAffiliateAnalytics(affiliateAnalyticsRes.data);
      setUsers(usersRes.data.users || []);
      setTestimonialStats(testimonialStatsRes.data || { pending: 0, approved: 0, rejected: 0 });
    } catch (error) {
      console.error('Error fetching admin data:', error);
      if (error.response?.status === 401 || error.response?.status === 403) {
        localStorage.removeItem('admin_token');
        setIsAuthenticated(false);
        toast.error('Admin session expired');
      }
    } finally {
      setIsLoading(false);
    }
  };
  
  const toggleAffiliateSystem = async () => {
    const token = localStorage.getItem('admin_token');
    try {
      await axios.put(
        `${API}/admin/affiliate/settings`,
        { ...affiliateSettings, enabled: !affiliateSettings.enabled },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAffiliateSettings(prev => ({ ...prev, enabled: !prev.enabled }));
      toast.success(`Affiliate system ${!affiliateSettings.enabled ? 'enabled' : 'disabled'}`);
    } catch (error) {
      toast.error('Failed to update settings');
    }
  };
  
  const handleAddLink = async () => {
    if (!newLink.supplement_name || !newLink.affiliate_url) {
      toast.error('Supplement name and URL are required');
      return;
    }
    
    const token = localStorage.getItem('admin_token');
    try {
      const res = await axios.post(
        `${API}/admin/affiliate/links`,
        newLink,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAffiliateLinks(prev => [...prev, res.data.link]);
      setShowAddLink(false);
      setNewLink({
        supplement_name: '',
        provider: 'amazon',
        affiliate_url: '',
        display_name: '',
        commission_rate: 0,
        enabled: true
      });
      toast.success('Affiliate link added');
    } catch (error) {
      toast.error('Failed to add link');
    }
  };
  
  // Load admin documents
  const loadDocuments = async () => {
    const token = localStorage.getItem('admin_token');
    if (!token) return;
    
    setLoadingDocuments(true);
    try {
      const res = await axios.get(`${API}/admin/documents`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDocuments(res.data.documents || []);
    } catch (error) {
      console.error('Failed to load documents:', error);
    } finally {
      setLoadingDocuments(false);
    }
  };
  
  // Load testimonials
  const loadTestimonials = async () => {
    const token = localStorage.getItem('admin_token');
    if (!token) return;
    
    setLoadingTestimonials(true);
    try {
      const [testimonialsRes, statsRes] = await Promise.all([
        axios.get(`${API}/testimonials/admin/all?status=${testimonialFilter}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/testimonials/admin/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setTestimonials(testimonialsRes.data || []);
      setTestimonialStats(statsRes.data);
    } catch (error) {
      console.error('Failed to load testimonials:', error);
    } finally {
      setLoadingTestimonials(false);
    }
  };
  
  // Approve testimonial
  const approveTestimonial = async (id) => {
    const token = localStorage.getItem('admin_token');
    try {
      await axios.put(`${API}/testimonials/admin/${id}/approve`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Testimonial approved');
      loadTestimonials();
    } catch (error) {
      toast.error('Failed to approve testimonial');
    }
  };
  
  // Reject testimonial
  const rejectTestimonial = async (id) => {
    const token = localStorage.getItem('admin_token');
    try {
      await axios.put(`${API}/testimonials/admin/${id}/reject`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Testimonial rejected');
      loadTestimonials();
    } catch (error) {
      toast.error('Failed to reject testimonial');
    }
  };
  
  // Delete testimonial
  const deleteTestimonial = async (id) => {
    const token = localStorage.getItem('admin_token');
    try {
      await axios.delete(`${API}/testimonials/admin/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Testimonial deleted');
      loadTestimonials();
    } catch (error) {
      toast.error('Failed to delete testimonial');
    }
  };
  
  // Download document
  const downloadDocument = async (doc) => {
    const token = localStorage.getItem('admin_token');
    try {
      const response = await axios.get(`${API}/admin/documents/${doc.id}/download`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`Downloaded ${doc.filename}`);
    } catch (error) {
      toast.error('Failed to download document');
    }
  };
  
  const handleUpdateLink = async () => {
    if (!editingLink) return;
    
    const token = localStorage.getItem('admin_token');
    try {
      await axios.put(
        `${API}/admin/affiliate/links/${editingLink.id}`,
        editingLink,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAffiliateLinks(prev => prev.map(l => l.id === editingLink.id ? editingLink : l));
      setEditingLink(null);
      toast.success('Link updated');
    } catch (error) {
      toast.error('Failed to update link');
    }
  };
  
  const handleDeleteLink = async (linkId) => {
    const token = localStorage.getItem('admin_token');
    try {
      await axios.delete(
        `${API}/admin/affiliate/links/${linkId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAffiliateLinks(prev => prev.filter(l => l.id !== linkId));
      toast.success('Link deleted');
    } catch (error) {
      toast.error('Failed to delete link');
    }
  };
  
  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user? This will remove all their data permanently.')) {
      return;
    }
    
    setDeletingUserId(userId);
    const token = localStorage.getItem('admin_token');
    try {
      await axios.delete(
        `${API}/admin/users/${userId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setUsers(prev => prev.filter(u => u.id !== userId));
      toast.success('User deleted successfully');
      fetchData(); // Refresh stats
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    } finally {
      setDeletingUserId(null);
    }
  };
  
  const filteredUsers = users.filter(user => 
    user.email?.toLowerCase().includes(userSearchTerm.toLowerCase()) ||
    user.name?.toLowerCase().includes(userSearchTerm.toLowerCase())
  );
  
  const handleChangeSecret = async (e) => {
    e.preventDefault();
    
    if (newSecret !== confirmSecret) {
      toast.error('New secrets do not match');
      return;
    }
    
    if (newSecret.length < 8) {
      toast.error('New secret must be at least 8 characters');
      return;
    }
    
    setChangingSecret(true);
    try {
      await axios.post(`${API}/admin/change-secret`, {
        current_secret: currentSecret,
        new_secret: newSecret
      });
      
      // Update the stored token with the new secret
      localStorage.setItem('admin_token', newSecret);
      
      toast.success('Admin secret changed successfully');
      setCurrentSecret('');
      setNewSecret('');
      setConfirmSecret('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change secret');
    } finally {
      setChangingSecret(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      // Validate admin secret
      const res = await axios.get(`${API}/admin/stats`, {
        headers: { Authorization: `Bearer ${adminSecret}` }
      });
      localStorage.setItem('admin_token', adminSecret);
      setIsAuthenticated(true);
      setStats(res.data);
      fetchData();
      toast.success('Admin access granted');
    } catch (error) {
      toast.error('Invalid admin credentials');
    } finally {
      setIsLoading(false);
    }
  };

  const handleApprove = async (providerId) => {
    try {
      await axios.post(
        `${API}/admin/approve-provider/${providerId}`,
        {},
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      toast.success('Provider approved successfully');
      setPendingProviders(prev => prev.filter(p => p.id !== providerId));
      setSelectedProvider(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve provider');
    }
  };

  const handleReject = async (providerId) => {
    if (!rejectReason.trim()) {
      toast.error('Please provide a reason for rejection');
      return;
    }
    
    try {
      await axios.post(
        `${API}/admin/reject-provider/${providerId}`,
        { reason: rejectReason },
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      toast.success('Provider rejected');
      setPendingProviders(prev => prev.filter(p => p.id !== providerId));
      setSelectedProvider(null);
      setRejectReason('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject provider');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    setIsAuthenticated(false);
    setAdminSecret('');
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#FAFAF9]">
        <header className="border-b border-border bg-white/80 backdrop-blur-sm">
          <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
            <Logo size="default" linkTo="/" />
          </div>
        </header>

        <main className="max-w-md mx-auto px-6 py-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-2xl shadow-sm border border-border p-8"
          >
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="w-8 h-8 text-red-600" />
              </div>
              <h1 className="text-2xl font-semibold text-[#1A1A1A]">Admin Access</h1>
              <p className="text-muted-foreground mt-2">
                Enter admin credentials to continue
              </p>
            </div>

            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Admin Secret</label>
                <Input
                  type="password"
                  value={adminSecret}
                  onChange={(e) => setAdminSecret(e.target.value)}
                  placeholder="Enter admin secret"
                  required
                  data-testid="admin-secret-input"
                />
              </div>

              <Button 
                type="submit" 
                disabled={isLoading} 
                className="w-full btn-primary"
                data-testid="admin-login-btn"
              >
                {isLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  'Access Admin Panel'
                )}
              </Button>
            </form>
            
            <div className="mt-6 text-center">
              <Link
                to="/admin/forgot-secret"
                className="text-sm text-primary hover:underline"
                data-testid="forgot-secret-link"
              >
                Forgot your admin secret?
              </Link>
            </div>
          </motion.div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      {/* Header */}
      <header className="border-b border-border bg-white/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-[#1A1A1A]">
            <Logo size="default" linkTo={null} className="pointer-events-none" />
            <span className="text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded-full ml-2">
              Admin
            </span>
          </Link>
          <Button variant="ghost" onClick={handleLogout} data-testid="admin-logout-btn">
            Logout
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-white rounded-xl border border-border p-4" data-testid="stat-total-users">
              <div className="flex items-center gap-2 text-muted-foreground mb-2">
                <Users className="w-4 h-4" />
                <span className="text-sm">Total Users</span>
              </div>
              <p className="text-2xl font-bold text-[#1A1A1A]">{stats.total_users || 0}</p>
            </div>
            <div className="bg-white rounded-xl border border-border p-4" data-testid="stat-total-assessments">
              <div className="flex items-center gap-2 text-muted-foreground mb-2">
                <Activity className="w-4 h-4" />
                <span className="text-sm">Assessments</span>
              </div>
              <p className="text-2xl font-bold text-[#1A1A1A]">{stats.total_assessments || 0}</p>
            </div>
            <div className="bg-white rounded-xl border border-border p-4" data-testid="stat-pending-providers">
              <div className="flex items-center gap-2 text-muted-foreground mb-2">
                <Clock className="w-4 h-4" />
                <span className="text-sm">Pending Providers</span>
              </div>
              <p className="text-2xl font-bold text-amber-600">{stats.pending_providers || 0}</p>
            </div>
            <div className="bg-white rounded-xl border border-border p-4" data-testid="stat-verified-providers">
              <div className="flex items-center gap-2 text-muted-foreground mb-2">
                <CheckCircle2 className="w-4 h-4" />
                <span className="text-sm">Verified Providers</span>
              </div>
              <p className="text-2xl font-bold text-green-600">{stats.verified_providers || 0}</p>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-1 bg-muted p-1 rounded-lg w-fit mb-6 flex-wrap">
          {['providers', 'users', 'testimonials', 'documents', 'affiliates', 'retailers', 'settings', 'overview'].map(tab => (
            <button
              key={tab}
              onClick={() => {
                setActiveTab(tab);
                if (tab === 'documents') loadDocuments();
                if (tab === 'testimonials') loadTestimonials();
              }}
              className={`px-4 py-2 rounded-md text-sm font-medium capitalize transition-colors ${
                activeTab === tab ? 'bg-white shadow-sm text-primary' : 'text-muted-foreground'
              }`}
              data-testid={`admin-tab-${tab}`}
            >
              {tab}
              {tab === 'testimonials' && testimonialStats.pending > 0 && (
                <span className="ml-1 px-1.5 py-0.5 bg-amber-100 text-amber-700 text-xs rounded-full">
                  {testimonialStats.pending}
                </span>
              )}
            </button>
          ))}
        </div>

        {activeTab === 'providers' && (
          <div className="grid md:grid-cols-3 gap-6">
            {/* Pending Providers List */}
            <div className="md:col-span-1">
              <div className="bg-white rounded-2xl border border-border p-4">
                <h2 className="font-semibold text-lg mb-4 flex items-center gap-2">
                  <Clock className="w-5 h-5 text-amber-600" />
                  Pending Verification
                </h2>
                
                {isLoading ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-primary" />
                  </div>
                ) : pendingProviders.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <CheckCircle2 className="w-8 h-8 mx-auto mb-2 text-green-500" />
                    <p className="text-sm">All caught up!</p>
                    <p className="text-xs mt-1">No pending providers</p>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-[60vh] overflow-y-auto">
                    {pendingProviders.map((provider) => (
                      <button
                        key={provider.id}
                        onClick={() => setSelectedProvider(provider)}
                        className={`w-full text-left p-3 rounded-xl transition-colors ${
                          selectedProvider?.id === provider.id
                            ? 'bg-primary/10 border border-primary/20'
                            : 'bg-muted/50 hover:bg-muted'
                        }`}
                        data-testid={`provider-item-${provider.id}`}
                      >
                        <p className="font-medium truncate">{provider.name}</p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {provider.specialty || 'No specialty'}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {formatDate(provider.created_at)}
                        </p>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Provider Details */}
            <div className="md:col-span-2">
              {!selectedProvider ? (
                <div className="bg-white rounded-2xl border border-border p-8 text-center">
                  <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <h2 className="text-xl font-semibold mb-2">Select a Provider</h2>
                  <p className="text-muted-foreground">
                    Click on a provider to review their application
                  </p>
                </div>
              ) : (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="space-y-6"
                >
                  {/* Provider Info */}
                  <div className="bg-white rounded-2xl border border-border p-6">
                    <h2 className="text-xl font-semibold mb-4">{selectedProvider.name}</h2>
                    
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-3">
                        <div className="flex items-center gap-2 text-sm">
                          <Mail className="w-4 h-4 text-muted-foreground" />
                          <span>{selectedProvider.email}</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm">
                          <Building2 className="w-4 h-4 text-muted-foreground" />
                          <span>{selectedProvider.specialty || 'Not specified'}</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm">
                          <FileText className="w-4 h-4 text-muted-foreground" />
                          <span>License: {selectedProvider.license_number || 'Not provided'}</span>
                        </div>
                      </div>
                      <div className="space-y-3">
                        <div className="flex items-center gap-2 text-sm">
                          <Clock className="w-4 h-4 text-muted-foreground" />
                          <span>Applied: {formatDate(selectedProvider.created_at)}</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm">
                          <CheckCircle2 className="w-4 h-4 text-green-500" />
                          <span className="text-green-600">Email Verified</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="bg-white rounded-2xl border border-border p-6">
                    <h3 className="font-semibold mb-4">Verification Actions</h3>
                    
                    <div className="flex gap-4 mb-6">
                      <Button 
                        onClick={() => handleApprove(selectedProvider.id)}
                        className="btn-primary flex items-center gap-2"
                        data-testid="approve-provider-btn"
                      >
                        <CheckCircle2 className="w-4 h-4" />
                        Approve Provider
                      </Button>
                    </div>

                    <div className="border-t border-border pt-4">
                      <p className="text-sm font-medium mb-2 flex items-center gap-2 text-red-600">
                        <XCircle className="w-4 h-4" />
                        Reject Application
                      </p>
                      <Textarea
                        value={rejectReason}
                        onChange={(e) => setRejectReason(e.target.value)}
                        placeholder="Reason for rejection (required)..."
                        className="mb-3"
                        data-testid="reject-reason-input"
                      />
                      <Button 
                        variant="destructive"
                        onClick={() => handleReject(selectedProvider.id)}
                        data-testid="reject-provider-btn"
                      >
                        Reject Provider
                      </Button>
                    </div>
                  </div>
                </motion.div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'users' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="space-y-6"
          >
            {/* Feedback Campaign Card */}
            <FeedbackCampaignCard API={API} />
            <div className="mt-6">
              <AnnouncementCampaignCard API={API} />
            </div>

            <div className="bg-white rounded-2xl border border-border p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold flex items-center gap-2">
                  <Users className="w-6 h-6 text-primary" />
                  User Management
                </h2>
                <span className="text-sm text-muted-foreground">{users.length} total users</span>
              </div>
              
              {/* Search */}
              <div className="relative mb-6">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={userSearchTerm}
                  onChange={(e) => setUserSearchTerm(e.target.value)}
                  placeholder="Search by email or name..."
                  className="pl-10"
                  data-testid="user-search-input"
                />
              </div>
              
              {/* Users List */}
              {isLoading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-primary" />
                </div>
              ) : filteredUsers.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <User className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>No users found</p>
                </div>
              ) : (
                <div className="space-y-2 max-h-[60vh] overflow-y-auto">
                  {filteredUsers.map((user) => (
                    <div 
                      key={user.id}
                      className="flex items-center gap-3 p-3 sm:p-4 bg-muted/50 rounded-xl hover:bg-muted transition-colors"
                    >
                      <div className="w-9 h-9 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                        <User className="w-4 h-4 text-primary" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{user.name || 'No name'}</p>
                        <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className="text-[10px] text-muted-foreground">Joined {user.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}</span>
                          {user.email_verified && (
                            <span className="text-[10px] text-green-600 flex items-center gap-0.5">
                              <CheckCircle2 className="w-3 h-3" /> Verified
                            </span>
                          )}
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteUser(user.id)}
                        disabled={deletingUserId === user.id}
                        className="text-red-500 hover:text-red-600 hover:bg-red-50 flex-shrink-0 p-2"
                        data-testid={`delete-user-${user.id}`}
                      >
                        {deletingUserId === user.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Trash2 className="w-4 h-4" />
                        )}
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        )}

        {/* Testimonials Tab */}
        {activeTab === 'testimonials' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="space-y-6"
          >
            {/* Stats Cards */}
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-center">
                <p className="text-2xl font-bold text-amber-600">{testimonialStats.pending}</p>
                <p className="text-sm text-amber-700">Pending Review</p>
              </div>
              <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-center">
                <p className="text-2xl font-bold text-green-600">{testimonialStats.approved}</p>
                <p className="text-sm text-green-700">Approved</p>
              </div>
              <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-center">
                <p className="text-2xl font-bold text-red-600">{testimonialStats.rejected}</p>
                <p className="text-sm text-red-700">Rejected</p>
              </div>
            </div>

            {/* Filter Buttons */}
            <div className="flex gap-2">
              {['pending', 'approved', 'rejected'].map(status => (
                <Button
                  key={status}
                  variant={testimonialFilter === status ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => {
                    setTestimonialFilter(status);
                    setTimeout(loadTestimonials, 0);
                  }}
                  className={testimonialFilter === status ? 'btn-primary' : ''}
                >
                  {status.charAt(0).toUpperCase() + status.slice(1)}
                </Button>
              ))}
            </div>

            {/* Testimonials List */}
            <div className="bg-white rounded-2xl border border-border p-6">
              <h2 className="text-xl font-semibold flex items-center gap-2 mb-6">
                <MessageSquare className="w-6 h-6 text-primary" />
                {testimonialFilter.charAt(0).toUpperCase() + testimonialFilter.slice(1)} Testimonials
              </h2>
              
              {loadingTestimonials ? (
                <div className="flex justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-primary" />
                </div>
              ) : testimonials.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <MessageSquare className="w-12 h-12 mx-auto mb-4 opacity-30" />
                  <p>No {testimonialFilter} testimonials</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {testimonials.map(testimonial => (
                    <div 
                      key={testimonial.id}
                      className="border border-border rounded-xl p-4"
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <span className="font-semibold text-[#1A1A1A]">{testimonial.name}</span>
                            <span className="text-sm text-muted-foreground">{testimonial.role}</span>
                            <div className="flex gap-0.5">
                              {[...Array(testimonial.rating)].map((_, i) => (
                                <Star key={i} className="w-3 h-3 fill-amber-400 text-amber-400" />
                              ))}
                            </div>
                          </div>
                          <p className="text-[#1A1A1A] mb-2">"{testimonial.text}"</p>
                          <p className="text-xs text-muted-foreground">
                            Submitted: {new Date(testimonial.created_at).toLocaleDateString()}
                            {testimonial.email && ` • ${testimonial.email}`}
                          </p>
                        </div>
                        <div className="flex gap-2 flex-shrink-0">
                          {testimonialFilter === 'pending' && (
                            <>
                              <Button
                                size="sm"
                                onClick={() => approveTestimonial(testimonial.id)}
                                className="bg-green-600 hover:bg-green-700 text-white"
                                data-testid={`approve-testimonial-${testimonial.id}`}
                              >
                                <CheckCircle2 className="w-4 h-4 mr-1" />
                                Approve
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => rejectTestimonial(testimonial.id)}
                                className="text-red-600 hover:bg-red-50"
                                data-testid={`reject-testimonial-${testimonial.id}`}
                              >
                                <XCircle className="w-4 h-4 mr-1" />
                                Reject
                              </Button>
                            </>
                          )}
                          {testimonialFilter === 'approved' && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => rejectTestimonial(testimonial.id)}
                              className="text-amber-600 hover:bg-amber-50"
                            >
                              Unpublish
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => deleteTestimonial(testimonial.id)}
                            className="text-red-500 hover:bg-red-50"
                            data-testid={`delete-testimonial-${testimonial.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        )}

        {activeTab === 'affiliates' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="space-y-6"
          >
            {/* Affiliate System Toggle */}
            <div className="bg-white rounded-2xl border border-border p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-semibold flex items-center gap-2">
                    <Link2 className="w-6 h-6 text-primary" />
                    Affiliate System
                  </h2>
                  <p className="text-sm text-muted-foreground mt-1">
                    {affiliateSettings?.enabled 
                      ? 'Affiliate links are visible to users on supplement recommendations' 
                      : 'Enable to show "Where to Buy" links on recommendations'}
                  </p>
                </div>
                <button
                  onClick={toggleAffiliateSystem}
                  className={`p-2 rounded-lg transition-colors ${
                    affiliateSettings?.enabled ? 'bg-green-100 text-green-600' : 'bg-muted text-muted-foreground'
                  }`}
                  data-testid="affiliate-toggle"
                >
                  {affiliateSettings?.enabled ? (
                    <ToggleRight className="w-8 h-8" />
                  ) : (
                    <ToggleLeft className="w-8 h-8" />
                  )}
                </button>
              </div>
              
              {affiliateSettings?.enabled && (
                <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700">
                  Affiliate links are now live. Users will see "Where to Buy" buttons on their recommendations.
                </div>
              )}
            </div>

            {/* Analytics */}
            {affiliateAnalytics && (
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-white rounded-xl border border-border p-4">
                  <div className="flex items-center gap-2 text-muted-foreground mb-2">
                    <MousePointerClick className="w-4 h-4" />
                    <span className="text-sm">Total Clicks</span>
                  </div>
                  <p className="text-2xl font-bold text-[#1A1A1A]">{affiliateAnalytics.total_clicks || 0}</p>
                </div>
                <div className="bg-white rounded-xl border border-border p-4">
                  <div className="flex items-center gap-2 text-muted-foreground mb-2">
                    <TrendingUp className="w-4 h-4" />
                    <span className="text-sm">Last 30 Days</span>
                  </div>
                  <p className="text-2xl font-bold text-primary">{affiliateAnalytics.recent_clicks_30d || 0}</p>
                </div>
                <div className="bg-white rounded-xl border border-border p-4">
                  <div className="flex items-center gap-2 text-muted-foreground mb-2">
                    <Link2 className="w-4 h-4" />
                    <span className="text-sm">Active Links</span>
                  </div>
                  <p className="text-2xl font-bold text-[#1A1A1A]">{affiliateLinks.filter(l => l.enabled).length}</p>
                </div>
              </div>
            )}

            {/* Affiliate Links Management */}
            <div className="bg-white rounded-2xl border border-border p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-semibold">Manage Affiliate Links</h2>
                <Button 
                  onClick={() => setShowAddLink(true)}
                  className="btn-primary flex items-center gap-2"
                  data-testid="add-affiliate-link-btn"
                >
                  <Plus className="w-4 h-4" />
                  Add Link
                </Button>
              </div>

              {/* Add/Edit Link Form */}
              {(showAddLink || editingLink) && (
                <div className="mb-6 p-4 bg-muted/50 rounded-xl border border-border">
                  <h3 className="font-medium mb-4">{editingLink ? 'Edit Link' : 'Add New Affiliate Link'}</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">Supplement Name</label>
                      <Input
                        value={editingLink?.supplement_name || newLink.supplement_name}
                        onChange={(e) => editingLink 
                          ? setEditingLink({...editingLink, supplement_name: e.target.value})
                          : setNewLink({...newLink, supplement_name: e.target.value})
                        }
                        placeholder="e.g., Magnesium Glycinate"
                        data-testid="affiliate-supplement-input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Provider</label>
                      <select
                        value={editingLink?.provider || newLink.provider}
                        onChange={(e) => editingLink
                          ? setEditingLink({...editingLink, provider: e.target.value})
                          : setNewLink({...newLink, provider: e.target.value})
                        }
                        className="w-full h-10 px-3 rounded-md border border-input bg-background"
                        data-testid="affiliate-provider-select"
                      >
                        <option value="amazon">Amazon</option>
                        <option value="iherb">iHerb</option>
                        <option value="vitacost">Vitacost</option>
                        <option value="custom">Custom</option>
                      </select>
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium mb-1">Affiliate URL</label>
                      <Input
                        value={editingLink?.affiliate_url || newLink.affiliate_url}
                        onChange={(e) => editingLink
                          ? setEditingLink({...editingLink, affiliate_url: e.target.value})
                          : setNewLink({...newLink, affiliate_url: e.target.value})
                        }
                        placeholder="https://www.amazon.com/dp/..."
                        data-testid="affiliate-url-input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Display Name (optional)</label>
                      <Input
                        value={editingLink?.display_name || newLink.display_name}
                        onChange={(e) => editingLink
                          ? setEditingLink({...editingLink, display_name: e.target.value})
                          : setNewLink({...newLink, display_name: e.target.value})
                        }
                        placeholder="e.g., Buy on Amazon"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Commission Rate (%)</label>
                      <Input
                        type="number"
                        value={editingLink?.commission_rate || newLink.commission_rate}
                        onChange={(e) => editingLink
                          ? setEditingLink({...editingLink, commission_rate: parseFloat(e.target.value) || 0})
                          : setNewLink({...newLink, commission_rate: parseFloat(e.target.value) || 0})
                        }
                        placeholder="e.g., 4.5"
                      />
                    </div>
                  </div>
                  <div className="flex gap-2 mt-4">
                    {editingLink ? (
                      <>
                        <Button onClick={handleUpdateLink} className="btn-primary">Save Changes</Button>
                        <Button variant="outline" onClick={() => setEditingLink(null)}>Cancel</Button>
                      </>
                    ) : (
                      <>
                        <Button onClick={handleAddLink} className="btn-primary" data-testid="save-affiliate-link-btn">Add Link</Button>
                        <Button variant="outline" onClick={() => setShowAddLink(false)}>Cancel</Button>
                      </>
                    )}
                  </div>
                </div>
              )}

              {/* Links List */}
              {affiliateLinks.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Link2 className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>No affiliate links yet</p>
                  <p className="text-sm mt-1">Add your first affiliate link to start monetizing</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {affiliateLinks.map(link => (
                    <div 
                      key={link.id}
                      className={`p-4 rounded-xl border ${link.enabled ? 'bg-white border-border' : 'bg-muted/50 border-muted'}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <p className="font-medium">{link.supplement_name}</p>
                            <span className={`text-xs px-2 py-0.5 rounded-full ${
                              link.enabled ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                            }`}>
                              {link.enabled ? 'Active' : 'Disabled'}
                            </span>
                          </div>
                          <div className="flex items-center gap-4 mt-1 text-sm text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <ShoppingCart className="w-3 h-3" />
                              {link.provider}
                            </span>
                            <span className="flex items-center gap-1">
                              <MousePointerClick className="w-3 h-3" />
                              {link.click_count || 0} clicks
                            </span>
                            {link.commission_rate > 0 && (
                              <span>{link.commission_rate}% commission</span>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground mt-1 truncate max-w-lg">
                            {link.affiliate_url}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setEditingLink(link)}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteLink(link.id)}
                            className="text-red-500 hover:text-red-600"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                          <a
                            href={link.affiliate_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-2 hover:bg-muted rounded-md"
                          >
                            <ExternalLink className="w-4 h-4 text-muted-foreground" />
                          </a>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Disclaimer Settings */}
            <div className="bg-white rounded-2xl border border-border p-6">
              <h3 className="font-semibold mb-4">Affiliate Disclaimer</h3>
              <p className="text-sm text-muted-foreground mb-3">
                This text appears near affiliate links to comply with FTC guidelines.
              </p>
              <Textarea
                value={affiliateSettings?.disclaimer_text || ''}
                onChange={(e) => setAffiliateSettings({...affiliateSettings, disclaimer_text: e.target.value})}
                className="mb-3"
                rows={2}
              />
              <Button 
                variant="outline" 
                size="sm"
                onClick={async () => {
                  const token = localStorage.getItem('admin_token');
                  try {
                    await axios.put(
                      `${API}/admin/affiliate/settings`,
                      affiliateSettings,
                      { headers: { Authorization: `Bearer ${token}` } }
                    );
                    toast.success('Disclaimer updated');
                  } catch (error) {
                    toast.error('Failed to update');
                  }
                }}
              >
                Save Disclaimer
              </Button>
            </div>
          </motion.div>
        )}

        {activeTab === 'retailers' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <RetailersTab />
          </motion.div>
        )}

        {activeTab === 'documents' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="max-w-4xl"
          >
            <div className="bg-white rounded-2xl border border-border p-6">
              <h2 className="text-xl font-semibold mb-6 flex items-center gap-2">
                <FolderOpen className="w-6 h-6 text-primary" />
                Admin Documents
              </h2>
              <p className="text-muted-foreground mb-6">
                Private documents for administrative use only. These files are not accessible to regular users.
              </p>
              
              {loadingDocuments ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-primary" />
                </div>
              ) : documents.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <File className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No documents available</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {documents.map((doc) => (
                    <div
                      key={doc.id}
                      className="flex items-center justify-between p-4 bg-muted/30 rounded-xl border border-border hover:border-primary/30 transition-colors"
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                          <FileText className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                          <h3 className="font-medium text-[#1A1A1A]">{doc.name}</h3>
                          <p className="text-sm text-muted-foreground">{doc.description}</p>
                          <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                            <span className="px-2 py-0.5 bg-primary/10 text-primary rounded-full">
                              {doc.category}
                            </span>
                            <span>{doc.size}</span>
                            <span>Created: {doc.created}</span>
                          </div>
                        </div>
                      </div>
                      <Button
                        onClick={() => downloadDocument(doc)}
                        variant="outline"
                        size="sm"
                        className="flex items-center gap-2"
                      >
                        <Download className="w-4 h-4" />
                        Download
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        )}

        {activeTab === 'settings' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="max-w-lg"
          >
            <div className="bg-white rounded-2xl border border-border p-6">
              <h2 className="text-xl font-semibold mb-6 flex items-center gap-2">
                <Settings className="w-6 h-6 text-primary" />
                Admin Settings
              </h2>
              
              {/* Change Admin Secret */}
              <div className="mb-8">
                <h3 className="text-lg font-medium mb-4 flex items-center gap-2">
                  <Key className="w-5 h-5 text-muted-foreground" />
                  Change Admin Secret
                </h3>
                
                <form onSubmit={handleChangeSecret} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Current Secret</label>
                    <Input
                      type="password"
                      value={currentSecret}
                      onChange={(e) => setCurrentSecret(e.target.value)}
                      placeholder="Enter current admin secret"
                      required
                      data-testid="current-secret-input"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-2">New Secret</label>
                    <Input
                      type="password"
                      value={newSecret}
                      onChange={(e) => setNewSecret(e.target.value)}
                      placeholder="Enter new admin secret"
                      minLength={8}
                      required
                      data-testid="new-secret-input"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Must be at least 8 characters
                    </p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-2">Confirm New Secret</label>
                    <Input
                      type="password"
                      value={confirmSecret}
                      onChange={(e) => setConfirmSecret(e.target.value)}
                      placeholder="Confirm new admin secret"
                      minLength={8}
                      required
                      data-testid="confirm-secret-input"
                    />
                  </div>
                  
                  <Button
                    type="submit"
                    disabled={changingSecret}
                    className="w-full btn-primary"
                    data-testid="change-secret-btn"
                  >
                    {changingSecret ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <Key className="w-4 h-4 mr-2" />
                    )}
                    {changingSecret ? 'Changing...' : 'Change Admin Secret'}
                  </Button>
                </form>
              </div>
              
              <div className="p-4 bg-amber-50 rounded-xl border border-amber-200 mb-8">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-amber-800">Important</p>
                    <p className="text-sm text-amber-700 mt-1">
                      After changing your admin secret, you'll need to use the new secret for all future logins.
                      Make sure to remember or securely store your new secret.
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Reset Platform Data - Danger Zone */}
              <div className="border-t border-red-200 pt-6">
                <h3 className="text-lg font-medium mb-4 flex items-center gap-2 text-red-600">
                  <AlertCircle className="w-5 h-5" />
                  Danger Zone
                </h3>
                
                <div className="p-4 bg-red-50 rounded-xl border border-red-200">
                  <p className="text-sm font-medium text-red-800 mb-2">Reset Platform Data</p>
                  <p className="text-sm text-red-700 mb-4">
                    This will permanently delete ALL users, assessments, journal entries, symptom logs, and provider data.
                    This action cannot be undone.
                  </p>
                  
                  <Button
                    type="button"
                    onClick={async () => {
                      const confirmText = prompt(
                        'Type "DELETE ALL DATA" to confirm you want to permanently delete all platform data:'
                      );
                      if (confirmText === 'DELETE ALL DATA') {
                        try {
                          const res = await axios.delete(`${API}/admin/reset-all-data`, {
                            headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` }
                          });
                          toast.success(`Deleted ${res.data.deleted.users} users and ${res.data.deleted.assessments} assessments`);
                          // Refresh stats
                          const statsRes = await axios.get(`${API}/admin/stats`, {
                            headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` }
                          });
                          setStats(statsRes.data);
                          setUsers([]);
                        } catch (err) {
                          toast.error(err.response?.data?.detail || 'Failed to reset data');
                        }
                      } else if (confirmText !== null) {
                        toast.error('Confirmation text did not match. Data was NOT deleted.');
                      }
                    }}
                    className="w-full bg-red-600 hover:bg-red-700 text-white"
                    data-testid="reset-all-data-btn"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete All Platform Data
                  </Button>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {activeTab === 'overview' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="bg-white rounded-2xl border border-border p-6"
          >
            <h2 className="text-xl font-semibold mb-6 flex items-center gap-2">
              <BarChart3 className="w-6 h-6 text-primary" />
              Platform Overview
            </h2>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div className="p-4 bg-muted/50 rounded-xl">
                <h3 className="font-medium mb-3">User Activity</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Total Users</span>
                    <span className="font-medium">{stats?.total_users || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Total Assessments</span>
                    <span className="font-medium">{stats?.total_assessments || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Journal Entries</span>
                    <span className="font-medium">{stats?.total_journal_entries || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Symptom Logs</span>
                    <span className="font-medium">{stats?.total_symptom_logs || 0}</span>
                  </div>
                </div>
              </div>
              
              <div className="p-4 bg-muted/50 rounded-xl">
                <h3 className="font-medium mb-3">Provider Status</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Pending Verification</span>
                    <span className="font-medium text-amber-600">{stats?.pending_providers || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Verified Providers</span>
                    <span className="font-medium text-green-600">{stats?.verified_providers || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Total Providers</span>
                    <span className="font-medium">{(stats?.pending_providers || 0) + (stats?.verified_providers || 0)}</span>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </main>
    </div>
  );
}
